import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import RelatorioExtracoesPrint from "./RelatorioExtracoesPrint";
import { auth, db } from "../Config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const estadoInicial = {
  codigo: "",
  dataRelatorio: new Date().toISOString().split("T")[0],
  dataExtracao: "",
  cliente: "",
  clienteId: "",
  clienteDocumento: "",
  obra: "",
  obraId: "",
  local: "",
  endereco: "",
  fckProjeto: "",
  quantidadeTestemunhos: "",
  horasAposExtracao: "",
  k4Observacao: "-0,04",
  conformidade: "-",
  observacoes: "",
  testemunhos: [],
};

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  const [ano, mes, dia] = dataISO.split("-");
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

function normalizarTexto(valor) {
  return String(valor || "").toLowerCase();
}

function obraEstaAtiva(obra = {}) {
  const status = normalizarTexto(obra.status || obra.Status || "");
  return obra.ativo !== false && !["inativo", "inativa", "desativado", "desativada", "cancelado", "cancelada"].includes(status);
}

function calcularProximoCodigo(lista) {
  const ano = new Date().getFullYear();
  const numeros = lista
    .map((item) => String(item.codigo || "").match(/CE-RE-ET-(\d+)\/\d{4}/))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter(Number.isFinite);

  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return `CE-RE-ET-${String(proximo).padStart(3, "0")}/${ano}`;
}

function criarTestemunhos(qtd, dataExtracao) {
  return Array.from({ length: Number(qtd || 0) }, (_, index) => ({
    peca: String(index + 1),
    identificacao: "",
    dataExtracao: dataExtracao || "",
    posicaoFace: "",
    diametro: "",
    altura: "",
    cargaRupturaTf: "",
    forcaRupturaN: "",
    tensaoInicial: "",
    k1: "",
    k2: "",
    k3: "",
    k4: "-0,04",
    tensaoCorrigida: "",
    massa: "",
    massaEspecifica: "",
    frenteCarbonatacao: "-",
    observacao: "",
  }));
}

export default function CadastroExtracoes() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [usuario, setUsuario] = useState(null);
  const [extracoes, setExtracoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obrasSistema, setObrasSistema] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [modoClonagem, setModoClonagem] = useState(false);
  const [clonarTudo, setClonarTudo] = useState(false);
  const [extracaoOrigemClone, setExtracaoOrigemClone] = useState(null);
  const [extracaoEditando, setExtracaoEditando] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [filtro, setFiltro] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [extracaoPrint, setExtracaoPrint] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [itensPerPage, setItensPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUsuario(null);
        return;
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      setUsuario(snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid });
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    carregarExtracoes();
    carregarClientes();
  }, []);

  async function carregarExtracoes() {
    setLoading(true);

    const q = query(collection(db, "tblExtracoesTestemunhos"), orderBy("criadoEm", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));

    setExtracoes(
      snap.docs.map((documento) => ({
        id: documento.id,
        ...documento.data(),
      }))
    );

    setLoading(false);
  }

  async function carregarClientes() {
    const q = query(collection(db, "clientes"), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));

    setClientes(
      snap.docs.map((documento) => ({
        id: documento.id,
        ...documento.data(),
      }))
    );
  }

  async function carregarObrasPorCliente(clienteId) {
    if (!clienteId) {
      setObrasSistema([]);
      return;
    }

    const q = query(
      collection(db, "obras"),
      where("clienteId", "==", clienteId),
      orderBy("nome", "asc")
    );
    const snap = await getDocs(consultaTenantAtual(q));

    setObrasSistema(
      snap.docs.map((documento) => ({
        id: documento.id,
        ...documento.data(),
      })).filter(obraEstaAtiva)
    );
  }

  function abrirNovoCadastro() {
    setModoEdicao(false);
    setModoClonagem(false);
    setClonarTudo(false);
    setExtracaoOrigemClone(null);
    setExtracaoEditando(null);
    setClienteSelecionado(null);
    setObrasSistema([]);
    setForm({
      ...estadoInicial,
    });
    setOpenDialog(true);
  }

  async function editarExtracao(extracao) {
    setModoEdicao(true);
    setModoClonagem(false);
    setClonarTudo(false);
    setExtracaoOrigemClone(null);
    setExtracaoEditando(extracao);

    const clienteEncontrado = clientes.find((cliente) => cliente.id === extracao.clienteId);
    setClienteSelecionado(clienteEncontrado || null);

    if (extracao.clienteId) {
      await carregarObrasPorCliente(extracao.clienteId);
    }

    setForm({
      ...estadoInicial,
      ...extracao,
      quantidadeTestemunhos:
        extracao.quantidadeTestemunhos || extracao.testemunhos?.length || "",
    });
    setOpenDialog(true);
  }

  function criarFormularioClone(extracao, incluirTudo = false) {
    const dadosClonados = { ...extracao };

    [
      "id",
      "codigo",
      "criadoEm",
      "criadoPor",
      "criadoPorEmail",
      "criadoPorNome",
      "atualizadoEm",
      "atualizadoPor",
      "atualizadoPorEmail",
      "atualizadoPorNome",
    ].forEach((campo) => delete dadosClonados[campo]);

    if (!incluirTudo) {
      delete dadosClonados.relatorioFotografico;
    }

    return {
      ...estadoInicial,
      ...dadosClonados,
      codigo: "",
      dataRelatorio: incluirTudo
        ? extracao.dataRelatorio || estadoInicial.dataRelatorio
        : estadoInicial.dataRelatorio,
      dataExtracao: incluirTudo ? extracao.dataExtracao || "" : "",
      horasAposExtracao: incluirTudo ? extracao.horasAposExtracao || "" : "",
      conformidade: incluirTudo ? extracao.conformidade || "-" : "-",
      testemunhos: incluirTudo && Array.isArray(extracao.testemunhos)
        ? extracao.testemunhos.map((testemunho) => ({ ...testemunho }))
        : [],
    };
  }

  async function clonarExtracao(extracao) {
    setModoEdicao(false);
    setModoClonagem(true);
    setClonarTudo(false);
    setExtracaoOrigemClone(extracao);
    setExtracaoEditando(null);

    const clienteEncontrado = clientes.find((cliente) => cliente.id === extracao.clienteId);
    setClienteSelecionado(clienteEncontrado || null);

    if (extracao.clienteId) {
      await carregarObrasPorCliente(extracao.clienteId);
    } else {
      setObrasSistema([]);
    }

    setForm(criarFormularioClone(extracao, false));
    setOpenDialog(true);
  }

  function alterarClonarTudo(event) {
    const marcado = event.target.checked;
    setClonarTudo(marcado);

    if (extracaoOrigemClone) {
      setForm(criarFormularioClone(extracaoOrigemClone, marcado));
    }
  }

  async function selecionarObra(obraId) {
    const obraSelecionada = obrasSistema.find((obra) => obra.id === obraId);

    setForm({
      ...form,
      obra: obraSelecionada?.nome || "",
      obraId: obraSelecionada?.id || "",
      endereco: obraSelecionada?.endereco || "",
      local: obraSelecionada?.endereco || obraSelecionada?.local || "",
    });
  }

  async function salvarExtracao() {
    if (!form.clienteId || !form.obraId) {
      alert("Selecione o cliente e a obra antes de salvar.");
      return;
    }

    if (!Number(form.quantidadeTestemunhos)) {
      alert("Informe a quantidade de testemunhos.");
      return;
    }

    const testemunhosAtuais = Array.isArray(form.testemunhos) ? form.testemunhos : [];
    const quantidade = Number(form.quantidadeTestemunhos);
    const testemunhosBase =
      testemunhosAtuais.length === quantidade
        ? testemunhosAtuais
        : criarTestemunhos(quantidade, form.dataExtracao);
    const testemunhos = form.dataExtracao
      ? testemunhosBase.map((testemunho) => ({
          ...testemunho,
          dataExtracao: testemunho.dataExtracao || form.dataExtracao,
        }))
      : testemunhosBase;
    const codigo = modoEdicao
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "extracoes_testemunhos",
          escopo: new Date(form.dataRelatorio || new Date()).getFullYear(),
          formato: {
            padding: 3,
            ano: new Date(form.dataRelatorio || new Date()).getFullYear(),
            template: "CE-RE-ET-{numero}/{ano}",
          },
          valorMinimo: obterMaiorSequencial(extracoes, /CE-RE-ET-(\d+)\/\d{4}/),
        })).codigo;

    const dados = {
      ...form,
      codigo,
      clienteDocumento: clienteSelecionado?.documento || form.clienteDocumento || "",
      quantidadeTestemunhos: quantidade,
      testemunhos,
      atualizadoEm: new Date(),
      atualizadoPor: usuario?.uid || null,
    };

    if (modoEdicao && extracaoEditando?.id) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblExtracoesTestemunhos", extracaoEditando.id),
        original: extracaoEditando,
        atual: { ...extracaoEditando, ...dados },
        metadados: { atualizadoEm: dados.atualizadoEm, atualizadoPor: dados.atualizadoPor },
      });
    } else {
      await addDoc(collection(db, "tblExtracoesTestemunhos"), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
        criadoPor: usuario?.uid || null,
      }));
    }

    setOpenDialog(false);
    setModoClonagem(false);
    setClonarTudo(false);
    setExtracaoOrigemClone(null);
    await carregarExtracoes();
  }

  async function abrirRelatorio(extracao, imprimir = false) {
    let extracaoAtualizada = extracao;

    if (extracao?.id) {
      const snap = await getDoc(doc(db, "tblExtracoesTestemunhos", extracao.id));
      if (snap.exists()) {
        extracaoAtualizada = { id: snap.id, ...snap.data() };
      }
    }

    if (!String(extracaoAtualizada?.horasAposExtracao || "").trim()) {
      alert("É necessário informar a quantidade de horas após a extração em que o CP foi rompido.");
      return;
    }

    setExtracaoPrint(extracaoAtualizada);
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  const extracoesFiltradas = useMemo(() => {
    const termo = normalizarTexto(filtro);
    if (!termo) return extracoes;

    return extracoes.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.local, item.dataExtracao, item.fckProjeto].some(
        (valor) => contemTextoPesquisa(normalizarTexto(valor), termo)
      )
    );
  }, [extracoes, filtro]);

  const inicio = currentPage * itensPerPage;
  const fim = inicio + itensPerPage;
  const extracoesPaginadas = extracoesFiltradas.slice(inicio, fim);

  if (modoPrint && extracaoPrint) {
    return (
      <RelatorioExtracoesPrint
        extracao={extracaoPrint}
        autoPrint={autoPrint}
        onVoltar={() => {
          setModoPrint(false);
          setAutoPrint(false);
          setExtracaoPrint(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Extrações de Testemunhos</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Lista de extrações cadastradas</Typography>
        <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovoCadastro}>
            + Nova Extração
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Código, cliente, obra, local ou data"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setCurrentPage(0);
            }}
            sx={{ flex: 1 }}
          />

          <TextField
            size="small"
            label="Qtd Extrações"
            value={extracoesFiltradas.length}
            InputProps={{ readOnly: true }}
            sx={{ width: { xs: "100%", md: 140 } }}
          />
        </Stack>
        </Paper>

        <Paper
          sx={{
            borderRadius: "12px",
            overflow: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: "none",
          }}
        >
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell>
                  <strong>Código</strong>
                </TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Local</TableCell>
                <TableCell align="center">Testemunhos</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {extracoesPaginadas.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ height: 48, cursor: "pointer" }}
                  onClick={() => history.push(`/app/extracoes-testemunhos/${item.id}`)}
                >
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataExtracao)}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.local || "-"}</TableCell>
                  <TableCell align="center">
                    {item.quantidadeTestemunhos || item.testemunhos?.length || 0}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          abrirRelatorio(item, false);
                        }}
                      >
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar cadastro">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          editarExtracao(item);
                        }}
                      >
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clonar extração">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          clonarExtracao(item);
                        }}
                      >
                        <ContentCopyIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblExtracoesTestemunhos" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRow><TableCell colSpan={6}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!loading && extracoesPaginadas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhuma extração cadastrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <TextField
            select
            size="small"
            label="Itens"
            value={itensPerPage}
            onChange={(e) => {
              setItensPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            sx={{ width: 120 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1}>
            <Button disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <Typography sx={{ display: "flex", alignItems: "center" }}>
              Página {currentPage + 1}
            </Typography>
            <Button
              disabled={fim >= extracoesFiltradas.length}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography component="span" variant="h6">
              {modoEdicao ? "Editar Extração" : "Nova Extração"}
            </Typography>
            {modoClonagem && (
              <FormControlLabel
                control={<Checkbox checked={clonarTudo} onChange={alterarClonarTudo} />}
                label="Clonar tudo"
                sx={{ m: 0 }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Código"
              value={form.codigo}
              onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              fullWidth
            />

            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option?.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clienteSelecionado}
              onChange={(event, newValue) => {
                setClienteSelecionado(newValue);
                setForm({
                  ...form,
                  cliente: newValue?.nome || "",
                  clienteId: newValue?.id || "",
                  clienteDocumento: newValue?.documento || "",
                  obra: "",
                  obraId: "",
                  local: "",
                  endereco: "",
                });
                carregarObrasPorCliente(newValue?.id);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Cliente" placeholder="Pesquisar cliente..." fullWidth />
              )}
            />

            <TextField
              select
              label="Empreendimento/Obra"
              value={form.obraId || ""}
              onChange={(e) => selecionarObra(e.target.value)}
              fullWidth
              disabled={!clienteSelecionado}
            >
              {obrasSistema.map((obra) => (
                <MenuItem key={obra.id} value={obra.id}>
                  {obra.nome}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Local"
              value={form.local}
              onChange={(e) => setForm({ ...form, local: e.target.value })}
              fullWidth
            />

            <TextField
              label="Data da extração"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dataExtracao}
              onChange={(e) => setForm({ ...form, dataExtracao: e.target.value })}
              fullWidth
            />

            <TextField
              label="Data do relatório"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dataRelatorio}
              onChange={(e) => setForm({ ...form, dataRelatorio: e.target.value })}
              fullWidth
            />

            <TextField
              label="Quantidade de testemunhos"
              type="number"
              value={form.quantidadeTestemunhos}
              onChange={(e) => setForm({ ...form, quantidadeTestemunhos: e.target.value })}
              fullWidth
            />

            <TextField
              label="FCK de projeto (MPa)"
              value={form.fckProjeto}
              onChange={(e) => setForm({ ...form, fckProjeto: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="contained" onClick={() => executarSalvamento(salvarExtracao)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
